/**
 * ContactPage
 *
 * Web port of Flutter's `ContactScreen`
 * (`lib/presentation/web/contact_screen.dart:32-323`). Three sections
 * (Support pointer / mail / request form) separated by thin dividers.
 * Submit-button flow matches Flutter exactly via `useContact.submit`:
 * validate → confirm → POST → result info sheet, with a full-page loading
 * overlay during the request. UI conventions (theme, responsive sizing,
 * sheets-not-dialogs) deviate from Flutter per `presentation.md`.
 *
 * The three body sections live in `./components/` to keep this file under
 * the ~200-line Should-Fix threshold from `presentation.md`.
 *
 * App-bar provenance: the ported `lib/presentation/web/contact_screen.dart`
 * has **no** `Scaffold.appBar` at all, so the bar is a pre-existing web-only
 * addition (`workflow/lessons.md` — a full-page web route needs a back
 * affordance or a deep-linked user is stranded). It is shaped after the
 * *mobile* WebView screen `lib/presentation/screens/legal/contact_screen.dart:106-123`
 * (back leading + centered title) and now renders through the shared
 * `ExpauseAppBar` (`presentation/components/common/appBar/ExpauseAppBar.tsx`).
 * This is not a Flutter-parity claim for the bar itself.
 *
 * Moving from the hand-rolled bar to `ExpauseAppBar` makes three intentional
 * visual changes: the title steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and the top safe-area inset is now honoured.
 *
 * Accessible without authentication; mounted under `PUBLIC_ROUTES`
 * (registered in Task 6).
 */
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { ContactSupportSection } from './components/ContactSupportSection';
import { ContactMailSection } from './components/ContactMailSection';
import { ContactRequestForm } from './components/ContactRequestForm';
import { Divider } from '@/presentation/components/common/Divider';
import { useContact } from './hooks/useContact';
import { useContactSheetSurface } from './hooks/useContactSheetSurface';
import { qaAttr } from '@/presentation/utils/qaAttr';

export const ContactPage = () => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const { l10n } = useLocale();
    const location = useLocation();

    // Canonical screen-key wiring (mirrors `LegalDocumentsPage`).
    const { currentScreenKeyRef } = useKeepAlive();
    const screenKeyRef = useRef<string>(
        currentScreenKeyRef.current || generateScreenKey(location.pathname)
    );
    const screenKey = screenKeyRef.current;

    useBasePage({ screenKey, onInit: () => { } });

    // Surface wiring for `useContact` — the hook owns state + orchestration,
    // the page picks the presentation surface. Both Contact and Support
    // share the same sheet surface (see `useContactSheetSurface`).
    const surface = useContactSheetSurface(screenKey);
    const { content, email, isLoading, setContent, setEmail, submit } = useContact(surface);

    // Deep-link guard: with no in-app history, fall back
    // to the feed (same pattern as LegalDocumentsPage).
    const handleBack = () => Navigator.goBackOr(() => Navigator.navigateToFeed());

    return (
        <div
            {...qaAttr({ id: 'contact', status: isLoading ? 'submitting' : 'idle' })}
            style={{
                position: 'relative',
                minHeight: '100vh',
                backgroundColor: theme.background,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* App bar — back button + centered title. */}
            <ExpauseAppBar
                rootQa={{ id: 'contact-app-bar' }}
                title={l10n.contact.title}
                leadingQa={{ id: 'contact-back' }}
                onLeadingTap={handleBack}
            />

            {/* Body — centered column with three sections. */}
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: `${responsive.spacing.lg}px`,
                }}
            >
                <div
                    style={{
                        width: '100%',
                        maxWidth: responsive.dialog.maxWidth,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: responsive.spacing.base,
                    }}
                >
                    <ContactSupportSection />

                    <Divider margin={responsive.spacing.lg} />

                    <ContactMailSection />

                    <Divider margin={responsive.spacing.lg} />

                    <ContactRequestForm
                        content={content}
                        email={email}
                        setContent={setContent}
                        setEmail={setEmail}
                        onSubmit={submit}
                    />
                </div>
            </div>

            {/* Loading overlay (kept in sync with `SupportPage`'s overlay).
                `position: 'fixed'` anchors both the dim layer *and* the
                centered spinner to the viewport rather than the
                document-relative parent — without this, when the user
                scrolls down to the submit button the spinner stays
                centered on the (taller-than-viewport) document and drifts
                off-screen, leaving only a dimmed page with no visible
                activity indicator. `zIndex: 9999` matches the project's
                existing full-viewport overlay convention
                (`VideoUploadProgressOverlay`, `EditUserProfilePage`) and
                sits well above sheet z-indices (sheets start at 100). */}
            {isLoading && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: theme.modalOverlay,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999,
                    }}
                >
                    <ExpauseLoadingIndicator />
                </div>
            )}
        </div>
    );
};
